import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Slider,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  ComposedChart,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { DemandSupplyNamedGroup, DemandSupplyResponse, DemandSupplyYearBreakdown } from '../../services/api';
import { SelectedDemandSupplyTypes } from '../../../types';

// Series colors, validated for CVD safety on both app surfaces.
// HDB is demand; condo/EC past SSD, new launches, and GLS are supply inputs.
export const DEMAND_SUPPLY_COLORS = {
  dark: { hdb: '#3987e5', hdbBto: '#3987e5', hdbResale: '#7aa7ff', condo: '#199e70', newLaunch: '#d95926', gls: '#c98500' },
  light: { hdb: '#2a78d6', hdbBto: '#2a78d6', hdbResale: '#5f8fe8', condo: '#1baf7a', newLaunch: '#eb6834', gls: '#eda100' },
};

const SSD_HOLDING_YEARS = 4; // purchases from 4 Jul 2025 onward hold 4 years (IRAS)
/** Search-radius slider granularity, in km — also the narrowest scan ring. */
const RADIUS_STEP_KM = 0.5;
type ChartView = 'yearly' | 'monthly' | 'cumulative';

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatMonth = (month: string): string => {
  const [y, m] = month.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
};

const addMonthsToKey = (month: string, months: number): string => {
  const [y, m] = month.split('-').map((v) => parseInt(v, 10));
  const total = y * 12 + (m - 1) + months;
  const ny = Math.floor(total / 12);
  const nm = (total % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
};

const formatUnits = (value: number): string =>
  value >= 10000 ? `${(value / 1000).toFixed(1)}k` : value.toLocaleString();

const estimateRefLabelWidth = (text: string): number => text.length * 5.6 + 12;

// Names shown inline per series before the tail is rolled up — enough to
// recognise the field without growing the tooltip past the chart.
const MAX_TOOLTIP_NAMES = 8;

// "NAVA GROVE 389 · PINETREE HILL 252 · +168 more 1,101" — the projects behind a
// competitive bar, in one wrapped line. Whatever is not named individually is
// rolled into the trailing "+N more", so the parts still add up to the bar.
const formatNamedGroup = (group: DemandSupplyNamedGroup | undefined): string | null => {
  if (!group) return null;
  const named = group.items.slice(0, MAX_TOOLTIP_NAMES);
  const rest = group.items.slice(MAX_TOOLTIP_NAMES);
  let otherCount = group.other?.count ?? 0;
  let otherUnits = group.other?.units ?? 0;
  rest.forEach((item) => {
    otherCount += 1;
    otherUnits += item.units;
  });
  const parts = named.map((item) => `${item.name} ${item.units.toLocaleString()}`);
  if (otherUnits > 0) parts.push(`+${otherCount} more ${otherUnits.toLocaleString()}`);
  return parts.length ? parts.join(' · ') : null;
};

// Recharts sets these as SVG presentation attributes, where nested CSS vars
// (e.g. --color-text-muted → rgba(var(--color-text-rgb), 0.7)) don't resolve and
// fall back to black — invisible in dark mode. Resolve them to concrete colors
// against the live theme (dark / light / classic) via a probe element, and
// re-resolve when the theme attribute changes.
interface ResolvedChartColors {
  axisLabel: string;
  grid: string;
  axisLine: string;
  zeroLine: string;
  cursor: string;
  accent: string;
  labelInk: string;
  labelChip: string;
  labelChipBorder: string;
  demand: string;
  supply: string;
}

const CHART_COLOR_TOKENS: Record<keyof ResolvedChartColors, string> = {
  axisLabel: 'var(--color-text-muted)',
  grid: 'rgba(var(--color-border-rgb), 0.1)',
  axisLine: 'rgba(var(--color-border-rgb), 0.3)',
  zeroLine: 'rgba(var(--color-border-rgb), 0.55)',
  cursor: 'rgba(var(--color-text-rgb), 0.35)',
  accent: 'var(--color-accent)',
  labelInk: 'var(--color-text-strong)',
  labelChip: 'var(--color-surface-elevated)',
  labelChipBorder: 'var(--color-border-strong)',
  demand: 'var(--ds-demand)',
  supply: 'var(--ds-supply)',
};

const FALLBACK_CHART_COLORS: ResolvedChartColors = {
  axisLabel: '#898781',
  grid: 'rgba(140, 135, 129, 0.16)',
  axisLine: 'rgba(140, 135, 129, 0.35)',
  zeroLine: 'rgba(140, 135, 129, 0.6)',
  cursor: 'rgba(195, 194, 183, 0.6)',
  accent: '#ed8936',
  labelInk: '#ffffff',
  labelChip: '#223046',
  labelChipBorder: 'rgba(255, 255, 255, 0.2)',
  demand: '#3987e5',
  supply: '#d95926',
};

const useResolvedChartColors = (rootRef: React.RefObject<HTMLElement>): ResolvedChartColors => {
  const [colors, setColors] = useState<ResolvedChartColors>(FALLBACK_CHART_COLORS);
  useEffect(() => {
    const resolve = () => {
      const host = rootRef.current;
      if (!host || typeof window === 'undefined') return;
      const probe = document.createElement('span');
      probe.style.display = 'none';
      host.appendChild(probe);
      const next = { ...FALLBACK_CHART_COLORS };
      (Object.keys(CHART_COLOR_TOKENS) as (keyof ResolvedChartColors)[]).forEach((key) => {
        probe.style.color = '';
        probe.style.color = CHART_COLOR_TOKENS[key];
        const resolved = getComputedStyle(probe).color;
        if (resolved) next[key] = resolved;
      });
      host.removeChild(probe);
      setColors(next);
    };
    resolve();
    const observer = new MutationObserver(resolve);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => observer.disconnect();
  }, [rootRef]);
  return colors;
};

// A reference-line annotation drawn as a filled chip so it stays legible over the
// plot in any theme. `accent` variant fills with the brand color + white text;
// `neutral` uses the surface chip + primary ink.
const makeRefLabel = (
  text: string,
  align: 'left' | 'right',
  variant: 'accent' | 'neutral',
  colors: ResolvedChartColors,
  lane = 0,
) => (props: any) => {
  const { viewBox } = props;
  if (!viewBox) return <g />;
  const charW = 5.6;
  const padX = 6;
  const height = 16;
  const width = text.length * charW + padX * 2;
  const gap = 4;
  const x = align === 'left' ? viewBox.x + gap : viewBox.x - width - gap;
  // Draw inside the reserved top margin rather than over the plotted values.
  const y = viewBox.y - height - 4 - lane * (height + 4);
  const fill = variant === 'accent' ? colors.accent : colors.labelChip;
  const stroke = variant === 'accent' ? colors.accent : colors.labelChipBorder;
  const ink = variant === 'accent' ? '#ffffff' : colors.labelInk;
  return (
    <g>
      <rect x={x} y={y} width={width} height={height} rx={4} fill={fill} stroke={stroke} strokeWidth={1} />
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={10}
        fontWeight={600}
        fill={ink}
      >
        {text}
      </text>
    </g>
  );
};

// A bar whose far end (away from the zero baseline) is rounded, so the
// diverging bars read as smooth capsules rather than hard rectangles.
// Recharts always passes a positive `height` with `y` at the top of the drawn
// rect, so 'top' rounds the upper edge (demand, above zero) and 'bottom' the
// lower edge (competitive pool, below zero).
const makeRoundedBar = (corner: 'top' | 'bottom') => (props: any) => {
  let { y, height } = props;
  const { x, width, fill } = props;
  // Recharts hands negative bars a negative height with `y` at the value end;
  // normalise to a top-left origin with positive height.
  if (height < 0) {
    y += height;
    height = -height;
  }
  if (!height || height <= 0 || !width) return <g />;
  const r = Math.max(0, Math.min(7, width / 2, height));
  const right = x + width;
  const bottom = y + height;
  const d =
    corner === 'top'
      ? `M${x},${bottom} L${x},${y + r} Q${x},${y} ${x + r},${y} L${right - r},${y} Q${right},${y} ${right},${y + r} L${right},${bottom} Z`
      : `M${x},${y} L${x},${bottom - r} Q${x},${bottom} ${x + r},${bottom} L${right - r},${bottom} Q${right},${bottom} ${right},${bottom - r} L${right},${y} Z`;
  return <path d={d} fill={fill} />;
};

interface DemandSupplyPanelProps {
  data: DemandSupplyResponse | null;
  loading: boolean;
  radius: number; // meters — outer edge of the scan ring
  /** Inner edge of the scan ring in meters; 0 scans from the centre outwards. */
  minRadius?: number;
  onRadiusChange?: (radius: number, minRadius: number) => void;
  /** Scan the whole island instead of the radius ring. */
  nationwide?: boolean;
  onNationwideChange?: (nationwide: boolean) => void;
  /** When true, hide the Search radius slider (host already controls radius). */
  hideRadiusSlider?: boolean;
  /** Match Map Research glass / Scan Radius slider chrome. */
  chrome?: 'default' | 'mapResearch';
  selectedTypes: SelectedDemandSupplyTypes;
  setSelectedTypes: (
    updater: ((prev: SelectedDemandSupplyTypes) => SelectedDemandSupplyTypes) | SelectedDemandSupplyTypes,
  ) => void;
}

const MAP_RESEARCH_MICRO_LABEL = {
  fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
  fontSize: 9.5,
  letterSpacing: '0.14em',
  textTransform: 'uppercase' as const,
  color: 'rgba(148, 197, 222, 0.8)',
};

const MAP_RESEARCH_SLIDER_SX = {
  display: 'block',
  height: 4,
  p: 0,
  mt: 1,
  mb: 0,
  color: '#8b5cf6',
  '& .MuiSlider-rail, & .MuiSlider-track': { height: 3 },
  '& .MuiSlider-thumb': { width: 12, height: 12, boxShadow: '0 0 8px rgba(139, 92, 246, 0.8)' },
  '& .MuiSlider-mark': { width: 3, height: 3, top: '50%' },
  '& .MuiSlider-markLabel': {
    color: 'rgba(148, 197, 222, 0.75)',
    fontSize: 10,
    fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
    top: 'calc(50% + 16px)',
    lineHeight: 1,
  },
};

const SUPER_RATIO_SLIDER_CELL_SX = {
  // Mark labels are absolutely positioned by MUI, so reserve their vertical
  // space explicitly instead of relying on breakpoint-specific offsets.
  pb: 3.5,
};

const DemandSupplyPanel: React.FC<DemandSupplyPanelProps> = ({
  data,
  loading,
  radius,
  minRadius = 0,
  onRadiusChange,
  nationwide = false,
  onNationwideChange,
  hideRadiusSlider = false,
  chrome = 'default',
  selectedTypes,
  setSelectedTypes,
}) => {
  const rootRef = React.useRef<HTMLDivElement>(null);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const chartColors = useResolvedChartColors(rootRef);
  const mapResearch = chrome === 'mapResearch';
  const [chartWidth, setChartWidth] = useState(0);

  useEffect(() => {
    const element = chartContainerRef.current;
    if (!element) return undefined;
    const updateWidth = () => setChartWidth(element.getBoundingClientRect().width);
    updateWidth();
    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);
    return () => observer.disconnect();
  }, [data, loading]);


  // Slider drags locally as an [inner, outer] km band; the fetch fires on release.
  const [radiusRangeKm, setRadiusRangeKm] = useState<[number, number]>([
    minRadius / 1000,
    radius / 1000,
  ]);
  useEffect(() => setRadiusRangeKm([minRadius / 1000, radius / 1000]), [minRadius, radius]);
  const [innerKm, outerKm] = radiusRangeKm;

  // Months between "today" and the assumed purchase date (0 = buying now).
  const [purchaseOffset, setPurchaseOffset] = useState(0);
  const [view, setView] = useState<ChartView>('yearly');

  const asOfMonth = data ? data.asOf.slice(0, 7) : null;
  const purchaseMonth = asOfMonth ? addMonthsToKey(asOfMonth, purchaseOffset) : null;
  const ssdExpiryMonth = purchaseMonth ? addMonthsToKey(purchaseMonth, SSD_HOLDING_YEARS * 12) : null;
  const ssdExpiryYear = ssdExpiryMonth ? ssdExpiryMonth.slice(0, 4) : null;

  const lineChartData = useMemo(() => {
    if (!data) return [];
    let cumDemand = 0;
    let cumSupply = 0;
    return data.timeline.map((p) => {
      const monthlyDemand =
        (selectedTypes.hdbBtoMop ? p.hdbBtoMop : 0) +
        (selectedTypes.hdbResaleMop ? p.hdbResaleMop : 0);
      const monthlySupply =
        (selectedTypes.condoSsd ? p.condoSsd : 0) +
        (selectedTypes.newLaunch ? p.newLaunch : 0) +
        (selectedTypes.gls ? p.gls : 0);
      cumDemand += monthlyDemand;
      cumSupply += monthlySupply;
      const demand = Math.round(view === 'cumulative' ? cumDemand : monthlyDemand);
      const supply = Math.round(view === 'cumulative' ? cumSupply : monthlySupply);
      return {
        month: p.month,
        demand,
        supply,
        supplyNeg: -supply, // plotted below the zero baseline (diverging)
      };
    });
  }, [data, selectedTypes, view]);

  const yearlyChartData = useMemo(() => {
    if (!data) return [];
    const rows = new Map<
      string,
      {
        year: string;
        hdbBtoDemand: number;
        hdbResaleDemand: number;
        condoSupply: number;
        newLaunchSupply: number;
        glsSupply: number;
        demand: number;
        supply: number;
      }
    >();

    data.timeline.forEach((p) => {
      const year = p.month.slice(0, 4);
      const row =
        rows.get(year) ||
        {
          year,
          hdbBtoDemand: 0,
          hdbResaleDemand: 0,
          condoSupply: 0,
          newLaunchSupply: 0,
          glsSupply: 0,
          demand: 0,
          supply: 0,
        };

      const hdbBtoDemand = selectedTypes.hdbBtoMop ? p.hdbBtoMop : 0;
      const hdbResaleDemand = selectedTypes.hdbResaleMop ? p.hdbResaleMop : 0;
      const condoSupply = selectedTypes.condoSsd ? p.condoSsd : 0;
      const newLaunchSupply = selectedTypes.newLaunch ? p.newLaunch : 0;
      const glsSupply = selectedTypes.gls ? p.gls : 0;

      row.hdbBtoDemand += hdbBtoDemand;
      row.hdbResaleDemand += hdbResaleDemand;
      row.condoSupply += condoSupply;
      row.newLaunchSupply += newLaunchSupply;
      row.glsSupply += glsSupply;
      row.demand += hdbBtoDemand + hdbResaleDemand;
      row.supply += condoSupply + newLaunchSupply + glsSupply;
      rows.set(year, row);
    });

    return Array.from(rows.values()).map((row) => ({
      ...row,
      hdbBtoDemand: Math.round(row.hdbBtoDemand),
      hdbResaleDemand: Math.round(row.hdbResaleDemand),
      condoSupply: Math.round(row.condoSupply),
      newLaunchSupply: Math.round(row.newLaunchSupply),
      glsSupply: Math.round(row.glsSupply),
      demand: Math.round(row.demand),
      supply: Math.round(row.supply),
      supplyNeg: -Math.round(row.supply), // plotted below the zero baseline (diverging)
    }));
  }, [data, selectedTypes]);

  const yearTicks = useMemo(
    () => lineChartData.filter((p) => p.month.endsWith('-01')).map((p) => p.month),
    [lineChartData],
  );

  const referenceLabelsOverlap = useMemo(() => {
    if (!purchaseMonth || !ssdExpiryMonth || lineChartData.length < 2 || chartWidth <= 0) return true;
    const buyIndex = lineChartData.findIndex((point) => point.month === purchaseMonth);
    const ssdIndex = lineChartData.findIndex((point) => point.month === ssdExpiryMonth);
    if (buyIndex < 0 || ssdIndex < 0) return false;

    // Mirror the chart's horizontal geometry closely enough to determine
    // whether the inward-facing label chips would intersect. The labels are
    // then allowed to share a lane whenever there is sufficient room.
    const plotWidth = Math.max(0, chartWidth - 64);
    const pointSpacing = plotWidth / (lineChartData.length - 1);
    const lineDistance = Math.abs(ssdIndex - buyIndex) * pointSpacing;
    const buyText = `Buy ${formatMonth(purchaseMonth)}`;
    const ssdText = `SSD ends ${formatMonth(ssdExpiryMonth)}`;
    const requiredDistance = estimateRefLabelWidth(buyText) + estimateRefLabelWidth(ssdText) + 12;
    return lineDistance < requiredDistance;
  }, [chartWidth, lineChartData, purchaseMonth, ssdExpiryMonth]);

  // Symmetric domain so the zero baseline sits in the middle and both
  // directions share one scale — the diverging bars/areas stay comparable.
  const activeChartData: Array<{ demand: number; supply: number }> =
    view === 'yearly' ? yearlyChartData : lineChartData;
  // Symmetric, round-number axis so the zero baseline is centered and both
  // sides share one scale. ~3 ticks per side, snapped to a nice step.
  const { yDomain, yTicks } = useMemo<{ yDomain: [number, number]; yTicks: number[] }>(() => {
    let maxAbs = 0;
    for (const p of activeChartData) maxAbs = Math.max(maxAbs, p.demand, p.supply);
    if (maxAbs <= 0) return { yDomain: [-100, 100], yTicks: [-100, 0, 100] };
    const rough = maxAbs / 3;
    const pow = Math.pow(10, Math.floor(Math.log10(rough)));
    const n = rough / pow;
    const step = (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * pow;
    const perSide = Math.ceil(maxAbs / step);
    const bound = step * perSide;
    const ticks: number[] = [];
    for (let i = -perSide; i <= perSide; i++) ticks.push(i * step);
    return { yDomain: [-bound, bound], yTicks: ticks };
  }, [activeChartData]);

  const toggleType = (key: keyof SelectedDemandSupplyTypes) =>
    setSelectedTypes((prev) => ({ ...prev, [key]: !prev[key] }));

  // Compact toggle pill — doubles as the series legend. Shapes match map markers
  // (circle / square / diamond). On = tinted + solid; off = dimmed + hollow.
  const typeChip = (
    key: keyof SelectedDemandSupplyTypes,
    label: string,
    colorVar: string,
    count: string | null,
    shape: 'circle' | 'square' | 'diamond' = 'circle',
  ) => {
    const on = selectedTypes[key];
    return (
      <Box
        role="button"
        aria-pressed={on}
        onClick={() => toggleType(key)}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.6,
          pl: 0.75,
          pr: 1,
          py: 0.35,
          borderRadius: 999,
          cursor: 'pointer',
          userSelect: 'none',
          border: '1px solid',
          borderColor: on ? colorVar : 'var(--color-border-strong)',
          bgcolor: on ? `color-mix(in srgb, ${colorVar} 14%, transparent)` : 'transparent',
          opacity: on ? 1 : 0.55,
          transition: 'opacity 0.12s ease, border-color 0.12s ease, background-color 0.12s ease',
          '&:hover': { opacity: 1, borderColor: colorVar },
        }}
      >
        <Box
          sx={{
            width: 9,
            height: 9,
            flexShrink: 0,
            borderRadius: shape === 'circle' ? '50%' : shape === 'square' ? '2px' : 0,
            transform: shape === 'diamond' ? 'rotate(45deg)' : 'none',
            bgcolor: on ? colorVar : 'transparent',
            border: on ? 'none' : `1.5px solid ${colorVar}`,
          }}
        />
        <Typography variant="caption" sx={{ color: 'var(--color-text)', fontWeight: on ? 600 : 400, whiteSpace: 'nowrap' }}>
          {label}
          {count && (
            <Box component="span" sx={{ ml: 0.5, color: 'var(--color-text-muted)', fontWeight: 400 }}>
              {count}
            </Box>
          )}
        </Typography>
      </Box>
    );
  };

  // Year → named competitive projects, for the tap/hover detail.
  const breakdownByYear = useMemo(() => {
    const map = new Map<string, DemandSupplyYearBreakdown>();
    data?.breakdown?.forEach((row) => map.set(String(row.year), row));
    return map;
  }, [data]);

  // A touch device has no hover, so the tooltip opens on tap instead —
  // otherwise the detail is unreachable on mobile. It covers the bars, so any
  // further tap (on the chart or anywhere else) dismisses it.
  const [tapToOpen, setTapToOpen] = useState(false);
  const [tapTipOpen, setTapTipOpen] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(hover: none)');
    const apply = () => setTapToOpen(query.matches);
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);
  useEffect(() => {
    if (!tapToOpen || !tapTipOpen) return undefined;
    const dismissOutside = (event: PointerEvent) => {
      // Taps inside the chart are handled by its own onClick toggle.
      if (chartContainerRef.current?.contains(event.target as Node)) return;
      setTapTipOpen(false);
    };
    document.addEventListener('pointerdown', dismissOutside, true);
    return () => document.removeEventListener('pointerdown', dismissOutside, true);
  }, [tapToOpen, tapTipOpen]);
  useEffect(() => setTapTipOpen(false), [view]);

  const totals = useMemo(() => {
    if (!data) return null;
    // A whole-island scan thins the marker lists for the map, so prefer the
    // server's totals — they count every unit found, capped markers included.
    if (data.totals) return data.totals;
    const hdbResaleUnits = data.demand.hdbResale?.reduce((s, b) => s + b.units, 0) ?? 0;
    return {
      hdbBtoUnits: data.demand.hdb.reduce((s, b) => s + b.units, 0),
      hdbResaleUnits,
      condoUnits: data.demand.condo.reduce((s, c) => s + c.units, 0),
      newLaunchUnits: data.supply.newLaunch.reduce((s, p) => s + p.availableUnits, 0),
      glsUnits: data.supply.gls.reduce((s, g) => s + (g.awarded && g.units ? g.units : 0), 0),
    };
  }, [data]);

  return (
    <Box
      ref={rootRef}
      sx={{
        // Chart series tokens; light theme overrides below.
        '--ds-demand': DEMAND_SUPPLY_COLORS.dark.hdb,
        '--ds-hdb-bto': DEMAND_SUPPLY_COLORS.dark.hdbBto,
        '--ds-hdb-resale': DEMAND_SUPPLY_COLORS.dark.hdbResale,
        '--ds-condo': DEMAND_SUPPLY_COLORS.dark.condo,
        '--ds-supply': DEMAND_SUPPLY_COLORS.dark.newLaunch,
        '--ds-gls': DEMAND_SUPPLY_COLORS.dark.gls,
        "[data-theme='light'] &": {
          '--ds-demand': DEMAND_SUPPLY_COLORS.light.hdb,
          '--ds-hdb-bto': DEMAND_SUPPLY_COLORS.light.hdbBto,
          '--ds-hdb-resale': DEMAND_SUPPLY_COLORS.light.hdbResale,
          '--ds-condo': DEMAND_SUPPLY_COLORS.light.condo,
          '--ds-supply': DEMAND_SUPPLY_COLORS.light.newLaunch,
          '--ds-gls': DEMAND_SUPPLY_COLORS.light.gls,
        },
        // Extra bottom space so the footnote isn't clipped inside scroll hosts.
        pb: mapResearch ? 1.5 : 0,
      }}
    >
      {/* Controls — grouped in one tidy card so the chart sits high on the panel */}
      <Box
        sx={{
          border: mapResearch ? '1px solid rgba(34, 211, 238, 0.22)' : '1px solid var(--color-border)',
          borderRadius: mapResearch ? '10px' : 2,
          bgcolor: mapResearch ? 'rgba(8, 18, 28, 0.35)' : 'transparent',
          px: 1.75,
          py: 1.25,
          mb: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {/* Drop both bounds and scan every block and project island-wide.
            Sits above the sliders so the two of them stay on one line. */}
        {!hideRadiusSlider && onNationwideChange && (
          <FormControlLabel
            sx={{ ml: 0, mr: 0, mb: -0.75 }}
            control={
              <Checkbox
                size="small"
                checked={nationwide}
                disabled={loading}
                onChange={(e) => onNationwideChange(e.target.checked)}
                sx={{ p: 0.5, color: 'var(--color-text-muted)', '&.Mui-checked': { color: 'var(--color-accent)' } }}
              />
            }
            label={
              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', ml: 0.5 }}>
                Whole Singapore
              </Typography>
            }
          />
        )}

        {/* Two sliders share a row on wider panels, stack when narrow */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: hideRadiusSlider
              ? '1fr'
              : { xs: '1fr', sm: '1fr 1fr' },
            columnGap: 3,
            rowGap: 1,
          }}
        >
          {/* Radius — omitted when the host map already owns radius */}
          {!hideRadiusSlider && (
            <Box sx={SUPER_RATIO_SLIDER_CELL_SX}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <Typography variant="caption" sx={{ color: 'var(--color-text-muted)' }}>
                  Search radius
                </Typography>
                <Typography variant="caption" sx={{ color: 'var(--color-accent)', fontWeight: 700 }}>
                  {nationwide
                    ? 'Whole Singapore'
                    : innerKm > 0
                      ? `${innerKm}–${outerKm} km`
                      : `${outerKm} km`}
                </Typography>
              </Box>
              {/* Drag both ends to scan a ring instead of a disc, e.g. 2–4 km. */}
              <Slider
                value={radiusRangeKm}
                min={0}
                max={10}
                step={0.5}
                size="small"
                disabled={loading || nationwide}
                disableSwap
                onChange={(_, v) => {
                  const [lo, hi] = v as number[];
                  // Keep at least one step between the ends so the ring never collapses.
                  if (hi - lo < RADIUS_STEP_KM) return;
                  setRadiusRangeKm([lo, hi]);
                }}
                onChangeCommitted={(_, v) => {
                  const [lo, hi] = v as number[];
                  onRadiusChange?.(hi * 1000, lo * 1000);
                }}
                marks={[0, 2, 5, 10].map((v) => ({ value: v, label: `${v}` }))}
                sx={{
                  display: 'block',
                  height: 4,
                  p: 0,
                  mt: 1,
                  mb: 0,
                  color: 'var(--color-accent)',
                  '& .MuiSlider-rail, & .MuiSlider-track': { height: 3 },
                  '& .MuiSlider-thumb': { width: 12, height: 12 },
                  '& .MuiSlider-mark': { width: 3, height: 3, top: '50%' },
                  '& .MuiSlider-markLabel': {
                    color: 'var(--color-text-muted)',
                    fontSize: 10,
                    top: 'calc(50% + 16px)',
                    lineHeight: 1,
                  },
                  '& .MuiSlider-markLabel[data-index="0"]': { transform: 'translateX(0)' },
                  '& .MuiSlider-markLabel[data-index="3"]': { transform: 'translateX(-100%)' },
                }}
              />
            </Box>
          )}
          {/* Buyer purchase timing → SSD expiry */}
          <Box sx={SUPER_RATIO_SLIDER_CELL_SX}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
              <Typography
                variant="caption"
                sx={
                  mapResearch
                    ? { ...MAP_RESEARCH_MICRO_LABEL, whiteSpace: 'nowrap' }
                    : { color: 'var(--color-text-muted)', whiteSpace: 'nowrap' }
                }
              >
                Buyer buys
              </Typography>
              <Typography
                variant="caption"
                sx={
                  mapResearch
                    ? {
                        fontSize: 12,
                        fontWeight: 800,
                        color: '#a78bfa',
                        whiteSpace: 'nowrap',
                      }
                    : { color: 'var(--color-text-strong)', fontWeight: 600, whiteSpace: 'nowrap' }
                }
              >
                {purchaseMonth ? formatMonth(purchaseMonth) : '—'}
                <Box
                  component="span"
                  sx={{
                    color: mapResearch ? 'rgba(222, 238, 246, 0.7)' : 'var(--color-text-muted)',
                    fontWeight: mapResearch ? 600 : 400,
                  }}
                >
                  {' → SSD '}
                </Box>
                <Box
                  component="span"
                  sx={{ color: mapResearch ? '#22d3ee' : 'var(--color-accent)', fontWeight: mapResearch ? 800 : 600 }}
                >
                  {ssdExpiryMonth ? formatMonth(ssdExpiryMonth) : '—'}
                </Box>
              </Typography>
            </Box>
            <Slider
              value={purchaseOffset}
              min={0}
              max={24}
              step={1}
              size="small"
              onChange={(_, v) => setPurchaseOffset(v as number)}
              marks={[
                { value: 0, label: 'Now' },
                { value: 12, label: '+1y' },
                { value: 24, label: '+2y' },
              ]}
              sx={
                mapResearch
                  ? MAP_RESEARCH_SLIDER_SX
                  : {
                      display: 'block',
                      height: 4,
                      p: 0,
                      mt: 1,
                      mb: 0,
                      color: 'var(--color-accent)',
                      '& .MuiSlider-rail, & .MuiSlider-track': { height: 3 },
                      '& .MuiSlider-thumb': { width: 12, height: 12 },
                      '& .MuiSlider-mark': { width: 3, height: 3, top: '50%' },
                      '& .MuiSlider-markLabel': {
                        color: 'var(--color-text-muted)',
                        fontSize: 10,
                        top: 'calc(50% + 16px)',
                        lineHeight: 1,
                      },
                      '& .MuiSlider-markLabel[data-index="0"]': { transform: 'translateX(0)' },
                      '& .MuiSlider-markLabel[data-index="2"]': { transform: 'translateX(-100%)' },
                    }
              }
            />
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !data ? (
        <Typography variant="body2" sx={{ color: 'var(--color-text-muted)', py: 4, textAlign: 'center' }}>
          No data available
        </Typography>
      ) : (
        <>
          {/* Chart header — the Upgrader legend sits top-left, over the upward
              demand bars it labels; the view toggle keeps the right. */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
              mb: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75 }}>
              <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 700, mr: 0.25 }}>
                Upgrader ▲
              </Typography>
              {typeChip('hdbBtoMop', 'BTO', 'var(--ds-hdb-bto)', totals ? formatUnits(totals.hdbBtoUnits) : null, 'circle')}
              {typeChip('hdbResaleMop', 'Resale', 'var(--ds-hdb-resale)', totals ? formatUnits(totals.hdbResaleUnits) : null, 'circle')}
            </Box>
            <ToggleButtonGroup
              value={view}
              exclusive
              size="small"
              onChange={(_, v) => v && setView(v)}
              sx={{ '& .MuiToggleButton-root': { py: 0.25, px: 1, fontSize: 11 } }}
            >
              <ToggleButton value="yearly">Year</ToggleButton>
              <ToggleButton value="monthly">Month</ToggleButton>
              <ToggleButton value="cumulative">Cumulative</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Box
            ref={chartContainerRef}
            sx={{ width: '100%', height: mapResearch ? 300 : 320, pb: mapResearch ? 1 : 0 }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={activeChartData}
                margin={{ top: 42, right: 12, bottom: mapResearch ? 20 : 4, left: 4 }}
                barCategoryGap="30%"
                stackOffset="sign"
                onClick={(state) => {
                  if (!tapToOpen) return;
                  setTapTipOpen((open) => !open && state?.activeTooltipIndex != null);
                }}
              >
                <defs>
                  <linearGradient id="dsDemandBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColors.demand} stopOpacity={0.95} />
                    <stop offset="100%" stopColor={chartColors.demand} stopOpacity={0.55} />
                  </linearGradient>
                  <linearGradient id="dsSupplyBar" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColors.supply} stopOpacity={0.55} />
                    <stop offset="100%" stopColor={chartColors.supply} stopOpacity={0.95} />
                  </linearGradient>
                  <linearGradient id="dsDemandArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColors.demand} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={chartColors.demand} stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="dsSupplyArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={chartColors.supply} stopOpacity={0.04} />
                    <stop offset="100%" stopColor={chartColors.supply} stopOpacity={0.5} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={chartColors.grid} vertical={false} />
                <XAxis
                  dataKey={view === 'yearly' ? 'year' : 'month'}
                  ticks={view === 'yearly' ? undefined : yearTicks}
                  tickFormatter={(v: string) => (view === 'yearly' ? v : v.slice(0, 4))}
                  tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  width={44}
                  domain={yDomain}
                  ticks={yTicks}
                  tickFormatter={(v: number) => formatUnits(Math.abs(v))}
                  tick={{ fill: chartColors.axisLabel, fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                {/* Zero baseline separating Potential HDB Upgrader (up) from Competitive Pool (down) */}
                <ReferenceLine y={0} stroke={chartColors.zeroLine} strokeWidth={1.5} />
                <Tooltip
                  trigger={tapToOpen ? 'click' : 'hover'}
                  active={tapToOpen ? tapTipOpen : undefined}
                  cursor={
                    view === 'yearly'
                      ? { fill: chartColors.grid }
                      : { stroke: chartColors.cursor, strokeDasharray: '3 3' }
                  }
                  content={({ active, payload, label }) => {
                    if (!active || !payload?.length) return null;
                    if (view === 'yearly') {
                      const point = payload[0].payload as {
                        hdbBtoDemand: number;
                        hdbResaleDemand: number;
                        condoSupply: number;
                        newLaunchSupply: number;
                        glsSupply: number;
                        demand: number;
                        supply: number;
                      };
                      const yearBreakdown = breakdownByYear.get(String(label));
                      const row = (
                        name: string,
                        value: number,
                        color: string,
                        group?: DemandSupplyNamedGroup,
                      ) => {
                        if (value <= 0) return null;
                        const names = formatNamedGroup(group);
                        return (
                          <Box key={name} sx={{ mt: 0.25 }}>
                            <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text)' }}>
                              <Box component="span" sx={{ color }}>●</Box> {name}: {value.toLocaleString()} units
                            </Typography>
                            {names && (
                              <Typography
                                variant="caption"
                                sx={{
                                  display: 'block',
                                  pl: 1.5,
                                  fontSize: 10.5,
                                  lineHeight: 1.35,
                                  color: 'var(--color-text-muted)',
                                }}
                              >
                                {names}
                              </Typography>
                            )}
                          </Box>
                        );
                      };
                      const gap = point.demand - point.supply;
                      return (
                        <Box
                          sx={{
                            bgcolor: 'var(--color-surface-elevated)',
                            border: '1px solid var(--color-border-strong)',
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                            boxShadow: 3,
                            maxWidth: 320,
                          }}
                        >
                          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', color: 'var(--color-text-strong)' }}>
                            {String(label)}
                          </Typography>
                          {row('BTO HDB MOP', point.hdbBtoDemand, 'var(--ds-hdb-bto)')}
                          {row('Resale HDB MOP', point.hdbResaleDemand, 'var(--ds-hdb-resale)')}
                          {row('Condo/EC past SSD', point.condoSupply, 'var(--ds-condo)', yearBreakdown?.condo)}
                          {row('New launch', point.newLaunchSupply, 'var(--ds-supply)', yearBreakdown?.newLaunch)}
                          {row('GLS pipeline', point.glsSupply, 'var(--ds-gls)', yearBreakdown?.gls)}
                          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'var(--color-text-muted)' }}>
                            Potential HDB Upgrader {point.demand.toLocaleString()} vs Competitive Pool {point.supply.toLocaleString()}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-muted)' }}>
                            {gap >= 0 ? `Potential HDB Upgrader +${gap.toLocaleString()}` : `Competitive Pool +${(-gap).toLocaleString()}`}
                          </Typography>
                        </Box>
                      );
                    }
                    const point = payload[0].payload as { demand: number; supply: number };
                    const gap = point.demand - point.supply;
                    return (
                      <Box
                        sx={{
                          bgcolor: 'var(--color-surface-elevated)',
                          border: '1px solid var(--color-border-strong)',
                          borderRadius: 1,
                          px: 1.5,
                          py: 1,
                          boxShadow: 3,
                        }}
                      >
                        <Typography variant="caption" fontWeight={600} sx={{ display: 'block', color: 'var(--color-text-strong)' }}>
                          {formatMonth(String(label))}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text)' }}>
                          <Box component="span" sx={{ color: 'var(--ds-demand)' }}>●</Box>{' '}
                          Potential HDB Upgrader: {point.demand.toLocaleString()} units
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text)' }}>
                          <Box component="span" sx={{ color: 'var(--ds-supply)' }}>●</Box>{' '}
                          Competitive Pool: {point.supply.toLocaleString()} units
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-muted)' }}>
                          {gap >= 0 ? `Potential HDB Upgrader +${gap.toLocaleString()}` : `Competitive Pool +${(-gap).toLocaleString()}`}
                        </Typography>
                      </Box>
                    );
                  }}
                />
                {view === 'yearly' ? (
                  <>
                    {/* Diverging bars: Potential HDB Upgrader above the baseline, Competitive Pool below */}
                    <Bar
                      dataKey="demand"
                      stackId="ds"
                      fill="url(#dsDemandBar)"
                      shape={makeRoundedBar('top')}
                      isAnimationActive={false}
                    />
                    <Bar
                      dataKey="supplyNeg"
                      stackId="ds"
                      fill="url(#dsSupplyBar)"
                      shape={makeRoundedBar('bottom')}
                      isAnimationActive={false}
                    />
                    {ssdExpiryMonth && ssdExpiryYear && yearlyChartData.some((p) => p.year === ssdExpiryYear) && (
                      <ReferenceLine
                        x={ssdExpiryYear}
                        stroke={chartColors.accent}
                        strokeWidth={2}
                        label={makeRefLabel(`SSD ends ${formatMonth(ssdExpiryMonth)}`, 'right', 'accent', chartColors, 0)}
                      />
                    )}
                  </>
                ) : (
                  <>
                    {/* Mirrored areas: Potential HDB Upgrader above the baseline, Competitive Pool below */}
                    <Area
                      type="monotone"
                      dataKey="demand"
                      stroke={chartColors.demand}
                      strokeWidth={2}
                      fill="url(#dsDemandArea)"
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                    <Area
                      type="monotone"
                      dataKey="supplyNeg"
                      stroke={chartColors.supply}
                      strokeWidth={2}
                      fill="url(#dsSupplyArea)"
                      dot={false}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                    {purchaseMonth && purchaseMonth >= data.window.start && purchaseMonth <= data.window.end && (
                      <ReferenceLine
                        x={purchaseMonth}
                        stroke={chartColors.cursor}
                        strokeDasharray="4 4"
                        label={makeRefLabel(`Buy ${formatMonth(purchaseMonth)}`, 'left', 'neutral', chartColors, 0)}
                      />
                    )}
                    {ssdExpiryMonth && ssdExpiryMonth >= data.window.start && ssdExpiryMonth <= data.window.end && (
                      <ReferenceLine
                        x={ssdExpiryMonth}
                        stroke={chartColors.accent}
                        strokeWidth={2}
                        label={makeRefLabel(
                          `SSD ends ${formatMonth(ssdExpiryMonth)}`,
                          'right',
                          'accent',
                          chartColors,
                          referenceLabelsOverlap ? 1 : 0,
                        )}
                      />
                    )}
                  </>
                )}
              </ComposedChart>
            </ResponsiveContainer>
          </Box>

          {/* Competitive legend — bottom-left, mirroring the downward supply bars. */}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 0.75,
              mt: 0.25,
            }}
          >
            <Typography variant="caption" sx={{ color: 'var(--color-text-muted)', fontWeight: 700, mr: 0.25 }}>
              Competitive ▼
            </Typography>
            {typeChip('condoSsd', 'Condo/EC', 'var(--ds-condo)', totals ? formatUnits(totals.condoUnits) : null, 'circle')}
            {typeChip('newLaunch', 'New launch', 'var(--ds-supply)', totals ? formatUnits(totals.newLaunchUnits) : null, 'square')}
            {typeChip('gls', 'GLS', 'var(--ds-gls)', totals ? formatUnits(totals.glsUnits) : null, 'diamond')}
          </Box>

          {/* The named projects behind a bar only exist in the yearly view. */}
          {view === 'yearly' && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.75,
                color: mapResearch ? 'rgba(148, 197, 222, 0.8)' : 'var(--color-text-muted)',
              }}
            >
              {tapToOpen ? 'Tap' : 'Hover'} a bar to see the projects behind it.
            </Typography>
          )}

          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 1.5,
              mb: mapResearch ? 0.5 : 0,
              pb: mapResearch ? 1 : 0,
              color: mapResearch ? 'rgba(196, 220, 233, 0.65)' : 'var(--color-text-subtle)',
              lineHeight: 1.45,
            }}
          >
            Potential HDB Upgrader: HDB blocks reaching 5-yr MOP (completion + 5, spread across the MOP year) and
            resale HDB purchases reaching 5-yr MOP from transaction date.
            Competitive Pool: private units past SSD (3 yrs if bought before 4 Jul 2025, 4 yrs after; EC new sales ≈ 8 yrs),
            unsold new launch units (from today), and awarded GLS units (12 months after award).
            {data.markersCapped && (
              <>
                {' '}
                Whole Singapore: every unit is counted in the chart and the totals above; the map
                plots the largest {(data.markerLimit ?? 0).toLocaleString()} HDB blocks and private
                addresses only.
              </>
            )}
          </Typography>
        </>
      )}
    </Box>
  );
};

export default DemandSupplyPanel;
